import React from 'react';
import { Platform, Pressable, View, Text } from 'react-native';
import { Dialog, XStack, YStack } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface CreditPackage {
  credits: number;
  price: number;
  popular?: boolean;
}

interface AICreditsModalProps {
  open: boolean;
  onClose: () => void;
  onPurchase?: (packageId?: number) => void;
  packages?: CreditPackage[];
}

const AICreditsModal: React.FC<AICreditsModalProps> = ({
  open,
  onClose,
  onPurchase,
  packages = [
    { credits: 100, price: 9.99, popular: false },
    { credits: 500, price: 39.99, popular: true },
    { credits: 1000, price: 69.99, popular: false },
  ]
}) => {
  const theme = themeSelector();

  return (
    <Dialog key="aiCredits" open={open}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              padding: 0,
              minWidth: 500,
              maxWidth: 600,
              maxHeight: "85%",
              borderRadius: 24,
              overflow: 'hidden',
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          {/* Header */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" gap="$3">
              <View style={{
                backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
                borderRadius: 12,
                padding: 10,
              }}>
                <MaterialIcons
                  name="smart-toy"
                  size={20}
                  color={theme ? "#0ea5e9" : "#38bdf8"}
                />
              </View>
              <YStack flex={1}>
                <Dialog.Title style={{
                  fontSize: 20,
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 4,
                  margin: 0,
                }}>
                  AI Credits Required
                </Dialog.Title>
                <Dialog.Description style={{
                  fontSize: 14,
                  color: theme ? '#64748b' : '#94a3b8',
                  fontWeight: '500',
                  margin: 0,
                }}>
                  Purchase credits to continue using AI features
                </Dialog.Description>
              </YStack>
            </XStack>
          </View>

          {/* Content */}
          <View style={{ padding: 24 }}>
            <YStack gap="$4">
              <View style={{
                backgroundColor: theme ? '#f8fafc' : '#374151',
                borderRadius: 16,
                padding: 20,
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}>
                <YStack gap="$3">
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 8,
                  }}>
                    You've run out of AI credits
                  </Text>
                  <Text style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    lineHeight: 22,
                  }}>
                    To continue using AI-powered features like content generation, analysis, and automation, you'll need to purchase additional credits.
                  </Text>
                </YStack>
              </View>

              {/* Credit Packages */}
              <YStack gap="$3">
                {packages.map((pkg, index) => (
                  <Pressable
                    key={index}
                    onPress={() => {
                      onPurchase?.(index);
                    }}
                    style={{
                      backgroundColor: pkg.popular
                        ? (theme ? '#eff6ff' : '#1e3a5f')
                        : (theme ? '#ffffff' : '#1e293b'),
                      borderRadius: 12,
                      padding: 16,
                      borderWidth: 2,
                      borderColor: pkg.popular
                        ? (theme ? '#3b82f6' : '#60a5fa')
                        : (theme ? '#e5e7eb' : '#4b5563'),
                    }}
                  >
                    <XStack alignItems="center" justifyContent="space-between">
                      <YStack flex={1}>
                        <XStack alignItems="center" gap="$2">
                          <Text style={{
                            fontSize: 18,
                            fontWeight: '700',
                            color: theme ? '#1e293b' : '#f1f5f9',
                          }}>
                            {pkg.credits} Credits
                          </Text>
                          {pkg.popular && (
                            <View style={{
                              backgroundColor: theme ? '#3b82f6' : '#2563eb',
                              borderRadius: 6,
                              paddingHorizontal: 8,
                              paddingVertical: 2,
                            }}>
                              <Text style={{
                                fontSize: 10,
                                fontWeight: '600',
                                color: '#ffffff',
                                textTransform: 'uppercase',
                              }}>
                                Popular
                              </Text>
                            </View>
                          )}
                        </XStack>
                        <Text style={{
                          fontSize: 14,
                          color: theme ? '#64748b' : '#94a3b8',
                          marginTop: 4,
                        }}>
                          ${(pkg.price / pkg.credits).toFixed(3)} per credit
                        </Text>
                      </YStack>
                      <Text style={{
                        fontSize: 24,
                        fontWeight: '800',
                        color: theme ? '#1e293b' : '#f1f5f9',
                      }}>
                        ${pkg.price}
                      </Text>
                    </XStack>
                  </Pressable>
                ))}
              </YStack>

              {/* Terms and Conditions */}
              <View style={{
                backgroundColor: theme ? '#fef3c7' : '#78350f',
                borderRadius: 12,
                padding: 16,
                borderWidth: 1,
                borderColor: theme ? '#f59e0b' : '#fbbf24',
                marginTop: 8,
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 }}>
                  <MaterialIcons
                    name="info-outline"
                    size={18}
                    color={theme ? '#92400e' : '#fbbf24'}
                    style={{ marginRight: 8, marginTop: 2 }}
                  />
                  <YStack flex={1}>
                    <Text style={{
                      fontSize: 13,
                      fontWeight: '600',
                      color: theme ? '#92400e' : '#fbbf24',
                      marginBottom: 6,
                    }}>
                      Terms & Conditions
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? '#78350f' : '#fcd34d',
                      lineHeight: 18,
                    }}>
                      By purchasing AI credits, you consent to the immediate provision of digital content and acknowledge that this waives your right to cancel under the Consumer Contracts Regulations 2013. Credits are non-refundable once purchased. Credits never expire and can be used across all AI features. Your statutory rights are unaffected - if credits are faulty or not as described, contact admin@smartsaas.co.uk for assistance.
                    </Text>
                  </YStack>
                </View>
              </View>
            </YStack>
          </View>

          {/* Footer Actions */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack gap="$3" justifyContent="flex-end">
              <Dialog.Close asChild>
                <Pressable
                  onPress={onClose}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#6b7280',
                    minWidth: 100,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: theme ? '#64748b' : '#d1d5db',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Cancel
                  </Text>
                </Pressable>
              </Dialog.Close>
            </XStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default AICreditsModal;

